/**
 * PATH: packages/ai-hub/src/hub/services/upload-service.ts
 * FILE_ID: AIHUB-PKG-UPLOAD-SVC-001
 * PURPOSE: Upload files to QDrive job folders from AI Hub
 * WHY: Users want to upload files directly from AI Hub to associate with jobs
 * FLOW:
 * ┌────────────┐  ┌─────────────────────────┐  ┌───────────────┐
 * │ File + Job │ → │ POST /api/qdrive/upload │ → │ FileRecord+S3 │
 * └────────────┘  └─────────────────────────┘  └───────────────┘
 * DEPENDENCIES:
 * - @quotech/api: API_BASE for backend URL
 * - streaming/auth: JWT token + tenant ID
 */

import { API_BASE } from '@quotech/api';
import { getAccessToken, getTenantId } from './streaming/auth';

export interface UploadResult {
  id: number;
  processingId: string;
  message: string;
  category: string;
  filename: string;
}

export interface JobOption {
  id: number;
  jobNumber: string;
  jobName: string;
  clientName: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export function uploadFileToJob(
  file: File,
  jobId: number,
  onProgress?: (progress: UploadProgress) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const token = getAccessToken();
    const tenantId = getTenantId();
    if (!token) { reject(new Error('Not authenticated')); return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('job_id', String(jobId));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/api/qdrive/files/upload/`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('X-Organisation-ID', tenantId);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({ loaded: e.loaded, total: e.total, percent: Math.round((e.loaded / e.total) * 100) });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({
            id: data.id,
            processingId: data.processing_id || '',
            message: data.message || 'Upload complete',
            category: data.category || 'unknown',
            filename: file.name,
          });
        } catch { reject(new Error('Invalid response from server')); }
      } else {
        const errMsg = xhr.status === 413
          ? 'File is too large'
          : xhr.status === 401
            ? 'Authentication expired — please refresh'
            : `Upload failed (${xhr.status})`;
        reject(new Error(errMsg));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error — check your connection')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    xhr.send(formData);
  });
}

export async function fetchJobsList(search?: string): Promise<JobOption[]> {
  const token = getAccessToken();
  const tenantId = getTenantId();
  if (!token) throw new Error('Not authenticated');

  const params = new URLSearchParams({ tenant: tenantId });
  if (search) params.set('search', search);

  const res = await fetch(`${API_BASE}/api/qdrive/jobs/list/?${params}`, {
    headers: { Authorization: `Bearer ${token}`, 'X-Organisation-ID': tenantId },
  });

  if (!res.ok) throw new Error(`Failed to load jobs (${res.status})`);

  const data = await res.json();
  const jobs: Array<{ id: number; job_number?: string; name?: string; client_name?: string }> =
    Array.isArray(data) ? data : data.results || data.jobs || [];

  return jobs.map((j) => ({
    id: j.id,
    jobNumber: j.job_number || String(j.id),
    jobName: j.name || 'Untitled',
    clientName: j.client_name || '',
  }));
}
